import type { EnhancedProductFamilyId, EnhancedProductVariantId } from '#api/enhancements/enhancedTmsApiTypes'
import type { BaseCheckResponse, ManCode } from '#api/tmsApiService'
import type { Brand } from '#types/Brand'

type LocalConfigurationId = Brand<`MAIC-${string}`, '__localConfigurationId'>
type EcmConfigurationVersion = Brand<number, '__EcmConfigurationVersion'>

export type CheckTaskId = Brand<string, '__taskId'>

export type OptionChangeSelectionId = `change_${string}` & { __optionChangeBrand: never }
type OptionChangeId = string & { __optionChangeBrand: never }

interface ModificationV1 {
  family: ModificationFamilyV1
}

interface ModificationFamilyV1 {
  id: EnhancedProductFamilyId
  title?: string
}

interface ModificationVariantV1 {
  id: EnhancedProductVariantId
  title: string
  price: number
}

interface RemovalV1 extends ModificationV1 {
  archetype: 'removal'
  fromVariant: ModificationVariantV1
}

interface AdditionV1 extends ModificationV1 {
  archetype: 'addition'
  toVariant: ModificationVariantV1
}

interface ChangeV1 extends ModificationV1 {
  archetype: 'change'
  fromVariant: ModificationVariantV1
  toVariant: ModificationVariantV1
}

interface OptionChangeConsequenceV1 {
  priceDelta: number
  priceSum: number
  additions: AdditionV1[]
  changes: ChangeV1[]
  removals: RemovalV1[]
}

interface OptionChangeQueryV1 {
  process: OptionChangeId
  started: Timestamp
  config: LocalConfigurationId
  type: 'change' | 'undo' | 'redo'
  optionSource: 'ecm' | 'national-code'
  family: ModificationFamilyV1
  fromVariant: ModificationVariantV1
  wasUserSelectedVariant: boolean
  toVariant: ModificationVariantV1
}

interface OptionChangeSucceededV1 extends OptionChangeQueryV1 {
  process: OptionChangeSelectionId
  status: 'success'
  type: 'change'
  ended: Timestamp
  conflicts: ((RemovalV1 | ChangeV1) & { lastSelectionTime: Timestamp })[]
  consequences: OptionChangeConsequenceV1
}

export type OptionChangeEntryV1 = OptionChangeSucceededV1

interface PrintOptionsV1 {
  doNotPrint: boolean
  emphasize: boolean
  printDescription: boolean
  previewImage: boolean
  imageOverrides: Record<string, boolean>
}

export interface ConfigurationProductFamilyV1 {
  selectedVariant?: { id: EnhancedProductVariantId; timestamp: Timestamp; previouslySelected?: EnhancedProductVariantId }
  isLocked: boolean
  printOptions: Partial<Record<string, PrintOptionsV1>>
}

export type TaskStatusV1 = 'queued' | 'progress' | 'success' | 'failure' | 'aborted' | 'unknown'

export interface CheckResultV1<TData extends BaseCheckResponse = BaseCheckResponse> {
  id: CheckTaskId
  status: TaskStatusV1
  startTime: Timestamp
  endTime?: Timestamp
  estimatedDuration?: number
  data?: TData
  error?: unknown
}

export interface CheckResults {
  ecmVersion: EcmConfigurationVersion
  status: TaskStatusV1
  checks: CheckResultV1[]
}

export type CheckTaskResultsByVersionV1 = Partial<Record<string, CheckResults>>

export type ConfigurationOptionalEquipmentId = Brand<`optional-equipment/${ReturnType<typeof crypto.randomUUID>}`, '__optionalEquipmentId'>
export interface ConfigurationOptionalEquipmentV1 {
  id: ConfigurationOptionalEquipmentId
  title: string
  description?: string
  variants: {
    family: { id: EnhancedProductFamilyId; title: string }
    sourceVariant?: { id: EnhancedProductVariantId; title: string; price: number | undefined }
    targetVariant: { id: EnhancedProductVariantId; title: string; price: number | undefined }
    version: number
  }[]
  implicitChanges: {
    family: { id: EnhancedProductFamilyId; title: string }
    sourceVariant: { id: EnhancedProductVariantId; title: string; price: number | undefined }
    targetVariant: { id: EnhancedProductVariantId; title: string; price: number | undefined }
    version: number
  }[]
  /**
   * Determines the state of the offering, meaning:
   * - removed: has been removed, will be ignored (currently keeping it, may as well)
   * - unchecked:
   */
  status: 'removed' | 'unchecked' | 'in-progress' | 'checked' | 'offered' | 'withdrawn'
  checkVersion?: number
}

export interface ConfigurationStorageDataV1 {
  type: 'tms-frontend-truck-configuration'
  version: '0.1.0'
  configurationVersion?: number
  title?: string
  configuredFamilies: Partial<Record<EnhancedProductFamilyId, ConfigurationProductFamilyV1>>
  history?: OptionChangeEntryV1[]
  initialSelection?: Record<EnhancedProductFamilyId, EnhancedProductVariantId>
  checks?: CheckTaskResultsByVersionV1
  optionalEquipment?: ConfigurationOptionalEquipmentV1[]
}

export function isConfigurationStorageDataV1(value: unknown): value is ConfigurationStorageDataV1 {
  if (!value || typeof value !== 'object') {
    return false
  }

  const data = value as { type: unknown; version: unknown }
  return data.type === 'tms-frontend-truck-configuration' && data.version === '0.1.0'
}

export function readInternalStoredDataV1(manCode: ManCode) {
  const data = manCode.configuration?.MAICInternal?.keyValuePairs?.find((pair) => pair.key === 'tms-frontend-truck-configuration')?.value

  const parsedData: unknown = data && JSON.parse(data)
  if (!isConfigurationStorageDataV1(parsedData)) {
    return undefined
  }

  return parsedData
}
